/* WeQuote Dev Board — shared card fragments. Faithful React ports of the
 * app.js HTML helpers (titleWithBug, assignedPill, dateLineHtml, designLineHtml,
 * checklistChip, requestLineHtml, sourceLineHtml, previewStatusChip, dateCell,
 * phaseProgressHtml, checklistHtml, commentsHtml, reqTableHtml, linksHtml,
 * detailTabsHtml). Rendering only — behaviour lives in core/. */
import React, { useState } from 'react'
import { useDevBoard } from '../DevBoardProvider'
import { IcUser, IcRestore, IcExternal } from '../icons'
import * as M from '../core/mutations'
import type { Feature } from '../core/types'
import { DL, EL, PL, isProgress, timeLabel } from '../core/constants'
import { fmtDate, daysLeft, daysAgo, daysAgoTxt, daysAgoLabel, daysLeftLabel, daysAwayLabel, deadlineCls, fmtTs, cmtSort } from '../core/dates'
import {
  activePhase, cardStatus, checklistProgress, reqCount, reqGroups,
  lastRequestedDate, lastChaseDate, requesterLabel, isLate,
} from '../core/logic'

export const TitleWithBug: React.FC<{ feat: Feature }> = ({ feat }) => (
  <>{feat.isBug && <span className="bug-pre">BUG: </span>}{feat.title}</>
)

export const AssignedPill: React.FC<{ dev: string; onClick?: (e: React.MouseEvent) => void }> = ({ dev, onClick }) =>
  dev && DL[dev]
    ? <span className={'assigned-pill' + (onClick ? ' qe-click' : '')} title={onClick ? 'Click to change the assigned dev' : undefined} onClick={onClick}><IcUser size={12} /> {DL[dev]}</span>
    : <span className={'assigned-pill' + (onClick ? ' qe-click' : '')} title={onClick ? 'Click to assign a dev' : undefined} onClick={onClick}>Unassigned</span>

export const DateLine: React.FC<{ feat: Feature; onDeadlineClick?: (e: React.MouseEvent) => void }> = ({ feat, onDeadlineClick }) => (
  <div className="date-line">
    {feat.start && <><span className="startd">Start date: {fmtDate(feat.start)}</span> &nbsp; </>}
    {feat.deadline
      ? <span className={'ddl' + (onDeadlineClick ? ' qe-text' : '')} title={onDeadlineClick ? 'Click to change the deadline' : undefined} onClick={onDeadlineClick}>Deadline: {fmtDate(feat.deadline)}</span>
      : <span className={'ddl none' + (onDeadlineClick ? ' qe-text' : '')} title={onDeadlineClick ? 'Click to set a deadline' : undefined} onClick={onDeadlineClick}>No deadline set</span>}
  </div>
)

export const DesignLine: React.FC<{ feat: Feature }> = ({ feat }) => {
  if (feat.designDeadline) {
    const dd = daysLeft(feat.designDeadline)
    return <div className="date-line"><span className="ddl">Design deadline: {fmtDate(feat.designDeadline)}</span> <span className={'dleft ' + dd.cls}>{dd.txt}</span></div>
  }
  return <div className="date-line"><span className="ddl none">No design deadline set</span></div>
}

export const ChecklistChip: React.FC<{ feat: Feature }> = ({ feat }) => {
  const p = checklistProgress(feat)
  if (!p.total) return null
  return <span className={'chk-chip' + (p.done === p.total ? ' done' : '')}>✓ {p.done}/{p.total}</span>
}

export const RequestLine: React.FC<{ feat: Feature }> = ({ feat }) => {
  if (isProgress(feat.status)) return null
  const c = reqCount(feat)
  if (!c) return null
  const la = daysAgo(lastRequestedDate(feat))
  const ca = daysAgo(lastChaseDate(feat))
  return (
    <div className="req-line">
      Requested {c} time{c === 1 ? '' : 's'}
      {la !== null && <> · last requested {daysAgoTxt(la)}</>}
      {ca !== null && ca <= 7 && <> · <span className="req-chase">chased {daysAgoTxt(ca)}</span></>}
    </div>
  )
}

export const SourceLine: React.FC<{ feat: Feature }> = ({ feat }) => {
  const s = feat.source
  if (!s) return null
  const txt = s.type === 'chat'
    ? 'From support chat · ' + (s.company || 'Unknown company') + (s.person ? ' · requested by ' + s.person : '')
    : 'Added manually by ' + (s.by || 'Unknown')
  const t = s.ts ? fmtTs(s.ts) : ''
  return <div className="dv-d-source">{txt}{t ? ' · ' + t : ''}</div>
}

export const RequesterWho: React.FC<{ feat: Feature }> = ({ feat }) => (
  <div className="rv-who">{requesterLabel(feat)}</div>
)

/** previewStatusChip — the ONE shared risk read. */
export const StChip: React.FC<{ feat: Feature }> = ({ feat }) => {
  const st = cardStatus(feat)
  return <span className={'st-chip st-' + st.cls}>{st.label}</span>
}

const DateCellView: React.FC<{ k: string; iso: string; sub?: string; cls?: string; onClick?: (e: React.MouseEvent) => void }> = ({ k, iso, sub, cls, onClick }) => (
  <div className="dv-d-date">
    <span className="dv-d-k">{k}</span>
    <span className={'dv-d-dv' + (cls ? ' ' + cls : '') + (onClick ? ' qe-text' : '')} title={onClick ? 'Click to change' : undefined} onClick={onClick}>{iso ? fmtDate(iso) : 'Not set'}</span>
    {iso && sub ? <span className="dv-d-sub2">{sub}</span> : null}
  </div>
)

/** phaseProgressHtml — progress block + dates per phase. */
export const PhaseProgress: React.FC<{ feat: Feature; onDeadlineClick?: (e: React.MouseEvent) => void; onStartClick?: (e: React.MouseEvent) => void; onProgressClick?: (e: React.MouseEvent) => void }> = ({ feat, onDeadlineClick, onStartClick, onProgressClick }) => {
  const ph = activePhase(feat)
  const chip = <StChip feat={feat} />
  if (ph === 'dev') {
    const barCls = feat.status === 'onhold' ? 'pbar-fill hold' : 'pbar-fill'
    return (
      <div className="dv-d-prog">
        <div className="dv-d-prog-top"><span className={'dv-d-pct' + (onProgressClick ? ' qe-text' : '')} title={onProgressClick ? 'Click to update progress' : undefined} onClick={onProgressClick}>{feat.progress || 0}%</span>{chip}</div>
        <div className={'pbar' + (onProgressClick ? ' qe-bar' : '')} title={onProgressClick ? 'Click to update progress' : undefined} onClick={onProgressClick}><div className={barCls} style={{ width: (feat.progress || 0) + '%' }} /></div>
        <div className="dv-d-dates">
          <DateCellView k="Date added" iso={feat.dateAdded} sub={daysAgoLabel(feat.dateAdded)} />
          <DateCellView k="Start date" onClick={onStartClick} iso={feat.start} />
          {feat.estCompletion ? <DateCellView k="Est. completion" iso={feat.estCompletion} sub={daysAwayLabel(feat.estCompletion)} cls={isLate(feat) ? 'late' : ''} /> : null}
          <DateCellView k="Deadline" onClick={onDeadlineClick} iso={feat.deadline} sub={daysLeftLabel(feat.deadline)} cls={deadlineCls(feat.deadline)} />
          {feat.status === 'live' ? <DateCellView k="Actual completion" iso={feat.completedOn} /> : null}
        </div>
      </div>
    )
  }
  if (ph === 'design') {
    return (
      <div className="dv-d-prog">
        <div className="dv-d-prog-top"><span className="dv-d-pct">{feat.designProgress || 0}% designed</span>{chip}</div>
        <div className="pbar"><div className="pbar-fill" style={{ width: (feat.designProgress || 0) + '%' }} /></div>
        <div className="dv-d-dates">
          <DateCellView k="Date added" iso={feat.dateAdded} sub={daysAgoLabel(feat.dateAdded)} />
          <DateCellView k="Design started" iso={feat.designStart} />
          <DateCellView k="Design deadline" iso={feat.designDeadline} sub={daysLeftLabel(feat.designDeadline)} cls={deadlineCls(feat.designDeadline)} />
          {feat.designCompletedOn ? <DateCellView k="Design completed" iso={feat.designCompletedOn} /> : null}
          <DateCellView k="Dev deadline" onClick={onDeadlineClick} iso={feat.deadline} sub={daysLeftLabel(feat.deadline)} cls={deadlineCls(feat.deadline)} />
        </div>
      </div>
    )
  }
  return (
    <div className="dv-d-prog dv-d-predev">
      <div className="dv-d-status-solo">{chip}</div>
      <div className="dv-d-dates">
        <DateCellView k="Date added" iso={feat.dateAdded} sub={daysAgoLabel(feat.dateAdded)} />
        <DateCellView k="Deadline" onClick={onDeadlineClick} iso={feat.deadline} sub={daysLeftLabel(feat.deadline)} cls={deadlineCls(feat.deadline)} />
      </div>
    </div>
  )
}

/** checklistHtml — gate list; locked once Live/Archived. */
export const ChecklistBlock: React.FC<{ feat: Feature }> = ({ feat }) => {
  const { mutate } = useDevBoard()
  const [text, setText] = useState('')
  const items = feat.checklist || []
  const locked = feat.status === 'live' || feat.status === 'archived'
  const p = checklistProgress(feat)
  const add = () => { if (text.trim()) { mutate((fs) => M.addCheckpoint(fs, feat.id, text)); setText('') } }
  return (
    <div className="chk-block">
      <div className="chk-hd">
        <span className="chk-title">Checklist</span>
        {p.total > 0 && <span className={'chk-count' + (p.done === p.total ? ' done' : '')}>{p.done}/{p.total}</span>}
      </div>
      <div className="chk-list">
        {items.length === 0 ? (
          <div className="chk-empty">No checkpoints yet.{locked ? '' : ' Add the steps that must be done before this can go Live.'}</div>
        ) : (
          items.map((c, i) => (
            <label key={i} className={'chk-item' + (c.done ? ' checked' : '')}>
              <input type="checkbox" checked={c.done} disabled={locked} onChange={() => mutate((fs) => M.toggleCheckpoint(fs, feat.id, i))} />
              <span className="chk-text">{c.text}</span>
              {!locked && <button className="chk-del" title="Remove" onClick={() => mutate((fs) => M.removeCheckpoint(fs, feat.id, i))}>×</button>}
            </label>
          ))
        )}
      </div>
      {!locked && (
        <div className="chk-add">
          <input className="chk-in" placeholder="Add a checkpoint…" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add() } }} />
          <button className="chk-btn" onClick={add}>Add checkpoint</button>
        </div>
      )}
    </div>
  )
}

/** commentsHtml — thread + composer (author = real logged-in agent). */
export const CommentsPane: React.FC<{ feat: Feature }> = ({ feat }) => {
  const { mutate, currentUserName } = useDevBoard()
  const [text, setText] = useState('')
  const cs = (feat.comments || []).slice().sort(cmtSort)
  const post = () => { if (text.trim()) { mutate((fs) => M.addComment(fs, feat.id, currentUserName, text)); setText('') } }
  return (
    <>
      <div className="cmt-thread">
        {cs.length === 0 ? (
          <div className="cmt-empty">No notes yet. Add the first one below.</div>
        ) : (
          cs.map((c, i) => (
            <div key={i} className="cmt">
              <div className="cmt-head"><span className="cmt-author">{c.author || 'Unknown'}</span><span className="cmt-time">{c.ts ? fmtTs(c.ts) : 'earlier'}</span></div>
              <div className="cmt-body">{c.text}</div>
            </div>
          ))
        )}
      </div>
      <div className="cmt-add">
        <textarea className="cmt-input" placeholder="Add a note or comment…" value={text} onChange={(e) => setText(e.target.value)} />
        <button className="cmt-btn" onClick={post}>Post</button>
      </div>
    </>
  )
}

/** reqTableHtml — companies × requester × first request × chases. */
export const ReqTable: React.FC<{ feat: Feature }> = ({ feat }) => {
  const { openSubModal } = useDevBoard()
  const groups = reqGroups(feat)
  const count = reqCount(feat)
  return (
    <>
      <div className="req-count-big">{count} compan{count === 1 ? 'y has' : 'ies have'} requested this</div>
      {groups.length === 0 ? (
        <div className="cmt-empty">No requests logged yet. Use “Add request / nudge” to log one.</div>
      ) : (
        <table className="req-table">
          <thead><tr><th>Company</th><th>Requester</th><th>First requested</th><th>Chased</th><th></th></tr></thead>
          <tbody>
            {groups.map((g, i) => (
              <tr key={i}>
                <td>{g.company || '—'}</td>
                <td>{g.person || '—'}</td>
                <td>{g.first ? fmtDate(g.first) : '—'}</td>
                <td className="req-td-chase">
                  {g.chases > 0
                    ? <button className="req-chase-btn" onClick={() => openSubModal({ kind: 'chaseHistory', id: feat.id, company: g.company, person: g.person })}>{g.chases} chase{g.chases === 1 ? '' : 's'}</button>
                    : <span className="req-none">—</span>}
                </td>
                <td className="req-td-log"><button className="req-log-btn" onClick={() => openSubModal({ kind: 'chaseLog', id: feat.id, company: g.company, person: g.person })}><IcRestore size={12} /> Chase</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

/** linksHtml — inline label/url editors + adder. */
export const LinksEditor: React.FC<{ feat: Feature }> = ({ feat }) => {
  const { mutate } = useDevBoard()
  const [label, setLabel] = useState('')
  const [url, setUrl] = useState('')
  const links = feat.links || []
  const add = () => { mutate((fs) => M.addLink(fs, feat.id, label, url)); setLabel(''); setUrl('') }
  return (
    <>
      <div className="lnk-list">
        {links.length === 0 ? (
          <span className="ov-empty">No links yet.</span>
        ) : (
          links.map((l, i) => (
            <div key={i} className="lnk-row">
              <input className="lnk-in lnk-edit lbl" defaultValue={l.label || ''} placeholder="Label" onBlur={(e) => { if (e.target.value !== l.label) mutate((fs) => M.editLink(fs, feat.id, i, 'label', e.target.value)) }} />
              <input className="lnk-in lnk-edit url" defaultValue={l.url || ''} placeholder="https://..." onBlur={(e) => { if (e.target.value !== l.url) mutate((fs) => M.editLink(fs, feat.id, i, 'url', e.target.value)) }} />
              <a href={l.url} target="_blank" rel="noopener noreferrer" className="lnk-open" title="Open link"><IcExternal size={13} /></a>
              <button className="lnk-del" title="Remove link" onClick={() => mutate((fs) => M.removeLink(fs, feat.id, i))}>×</button>
            </div>
          ))
        )}
      </div>
      <div className="lnk-add">
        <input placeholder="Label (e.g. Figma)" className="lnk-in" value={label} onChange={(e) => setLabel(e.target.value)} />
        <input placeholder="https://..." className="lnk-in" value={url} onChange={(e) => setUrl(e.target.value)} />
        <button className="lnk-btn" onClick={add}>Add</button>
      </div>
    </>
  )
}

/** detailTabsHtml — Notes / Request Details. */
export const DetailTabs: React.FC<{ feat: Feature }> = ({ feat }) => {
  const { detailTab, setDetailTab } = useDevBoard()
  const notesActive = detailTab !== 'requests'
  return (
    <div className="dtab-wrap">
      <div className="dtab-bar">
        <button className={'dtab' + (notesActive ? ' active' : '')} onClick={() => setDetailTab('notes')}>Notes</button>
        <button className={'dtab' + (!notesActive ? ' active' : '')} onClick={() => setDetailTab('requests')}>Request Details</button>
      </div>
      <div className="dtab-body">{notesActive ? <CommentsPane feat={feat} /> : <ReqTable feat={feat} />}</div>
    </div>
  )
}

/* re-exported bits used by views */
export { EL, PL, timeLabel }
